import { FC, type JSX, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import {
  FlowRadioGroup,
  Header,
  Layout,
  NextButton,
  STEP_TITLE_ID,
  getRadioGroupNameProps,
} from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioTile, TextLink, VisuallyHidden } from '@eneco/sparky';
import { SingleMeter, SmartMeter } from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { MeterTypeStepRendering } from '@sitecore/types/MeterTypeStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';

const METER_TYPE_EXPLANATION_ID = 'meter-type-explanation';

interface FormFields {
  hasDoubleMeter: string;
}

const generateInputElement = (value: string, label: string, description?: string): JSX.Element => {
  let icon: JSX.Element;

  if (value === 'double') {
    icon = <SmartMeter size="small" />;
  } else {
    icon = <SingleMeter size="small" />;
  }

  return (
    <RadioTile key={value} value={value} icon={icon} subtitle={description || undefined}>
      {label}
    </RadioTile>
  );
};

export const MeterTypeStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const {
    context: { hasDoubleMeter },
  } = flowState;

  const { MeterTypeStep: { fields: textualData } = {} } = usePlaceholderContent<{
    MeterTypeStep: MeterTypeStepRendering;
  }>();

  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const formSchema = yup.object({
    hasDoubleMeter: requiredRadioChoice(textualData?.meterType?.meterTypeRadio?.value.requiredMessage),
  });

  const toMeterTypeValue = (isDouble?: boolean) => {
    if (isDouble === undefined) return undefined;

    return isDouble ? 'double' : 'single';
  };

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasDoubleMeter: toMeterTypeValue(hasDoubleMeter),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const watchHasDoubleMeter = watch('hasDoubleMeter');

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const currentHasDoubleMeter = formData.hasDoubleMeter === 'double';
      const shouldResetSelectedOffer = currentHasDoubleMeter !== hasDoubleMeter;

      if (shouldResetSelectedOffer) {
        updateContextAndGoToNextStep({
          hasDoubleMeter: formData.hasDoubleMeter === 'double',
          discountCode: undefined,
        });
      } else {
        updateContextAndGoToNextStep({
          hasDoubleMeter: formData.hasDoubleMeter === 'double',
        });
      }
    }
  }, [isSubmitSuccessful, hasDoubleMeter]);

  useEffect(() => {
    setShouldShowOfferAvailabilityNotification(
      watchHasDoubleMeter === 'single' &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content,
    );
  }, [watchHasDoubleMeter]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title id={STEP_TITLE_ID}>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && (
          <Header.Text>
            {textualData.content.text.value}{' '}
            <TrackedDialog
              title={textualData?.meterType?.dialog?.value?.title}
              trigger={<TextLink>{textualData?.meterType?.dialog?.value?.triggerText}</TextLink>}>
              <RichText html={textualData?.meterType?.dialog?.value?.content} />
            </TrackedDialog>
          </Header.Text>
        )}
      </Header>

      {textualData?.meterType?.dialog?.value?.content && (
        <VisuallyHidden id={METER_TYPE_EXPLANATION_ID}>
          <RichText html={textualData.meterType.dialog.value.content} />
        </VisuallyHidden>
      )}

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <FlowRadioGroup
        control={control}
        name="hasDoubleMeter"
        {...getRadioGroupNameProps(textualData?.meterType?.meterTypeRadio?.value?.label)}
        hint={textualData?.meterType?.meterTypeRadio?.value?.hint || undefined}
        direction="column"
        error={errors['hasDoubleMeter']?.message}>
        {textualData?.meterType?.meterTypeRadio?.value?.options?.map(option =>
          generateInputElement(option.name, option.label, option.description),
        )}
      </FlowRadioGroup>

      <div aria-live="polite" role="status">
        {shouldShowOfferAvailabilityNotification && (
          <TrackedNotificationBox
            isAlert={false}
            label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
            title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
            text={<RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />}
            variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
          />
        )}
      </div>

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
